'use client';

import { usePathname } from 'next/navigation';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { LOCALES } from '@/i18nConfig';
import Link from '@/components/atoms/Link';
import { ChevronDownIcon } from '@/public/assets/svg';
import { getCountryFromLocale } from '@/app/api/utils';
import { Lang } from '@/types/locales';
import { BreadCrumbProps, BreadcrumbItem } from './types';
const Breadcrumbs = ({
  homeBreadcrumbLabel,
  separator,
  containerClasses,
  listClasses,
  activeClasses,
  start = 0,
  lang,
  currentItem
}: BreadCrumbProps) => {
  const {
    t
  } = useTranslation();
  const pathname = usePathname();
  const processPath = () => {
    // Remove language code from path
    const paths = pathname.replace(new RegExp(`(${Object.values(LOCALES).join('|')})`, 'i'), '');
    const strippedPath = Object.values(LOCALES).reduce((acc, locale) => {
      if (acc.startsWith(`/${locale}`)) {
        return acc.replace(`/${locale}`, '');
      }
      return acc;
    }, paths);
    return strippedPath.split('/').filter(path => path).slice(start);
  };
  const formatSegmentLabel = (segment: string): string => {
    const label = segment.replace(/[^A-Z0-9]/gi, ' ');

    // Handle special cases
    if (label === 'guide') {
      return t('common.guides');
    }
    if (label === 'blog') {
      return t('common.blogs');
    }
    if (label === 'bitcoin atm') {
      // Hong Kong locale has different handling
      if (lang === LOCALES.EN_HK || lang === LOCALES.ZH_HK) {
        return '';
      }
      return getCountryFromLocale((lang || LOCALES.EN_CA) as Lang);
    }
    return label;
  };
  const buildBreadcrumbItems = (): BreadcrumbItem[] => {
    const pathSegments = processPath();
    const items: BreadcrumbItem[] = [];
    if (homeBreadcrumbLabel) {
      items.push({
        label: homeBreadcrumbLabel,
        url: '/'
      });
    }
    for (let i = 0; i < pathSegments.length; i++) {
      // Skip last path segment if we have a currentItem
      if (i === pathSegments.length - 1 && currentItem) {
        continue;
      }
      const href = `/${pathSegments.slice(0, i + 1).join('/')}`;
      const label = formatSegmentLabel(pathSegments[i]);

      // Skip empty labels (e.g., for bitcoin-atm in HK)
      if (!label) {
        continue;
      }
      items.push({
        label,
        url: href
      });
    }
    if (currentItem) {
      items.push({
        label: currentItem.label,
        url: currentItem.url || null
      });
    }
    return items;
  };
  const breadcrumbItems = buildBreadcrumbItems();
  const getPreviousItem = (): BreadcrumbItem => {
    if (breadcrumbItems.length > 1) {
      return breadcrumbItems[breadcrumbItems.length - 2];
    }

    // Fallback when no previous item exists
    const pathSegments = processPath();
    return {
      label: pathSegments.length > 1 ? pathSegments[pathSegments.length - 2] : pathSegments[0] || '',
      url: `/${pathSegments.slice(0, -1).join('/')}`
    };
  };
  const previousItem = getPreviousItem();
  const showMobileBackLink = currentItem || processPath().length >= 4;
  return <div data-sentry-component="Breadcrumbs" data-sentry-source-file="index.tsx">
      <ul className={containerClasses}>
        <div className="items-center lg:flex">
          {breadcrumbItems.map((item, index) => <li key={index} className={clsx('hidden items-center lg:flex')}>
              <p className={index === breadcrumbItems.length - 1 ? `${listClasses} ${activeClasses}` : listClasses}>
                <Link className="uppercase" href={item.url || pathname}>
                  {item.label}
                </Link>
              </p>
              {index < breadcrumbItems.length - 1 && separator}
            </li>)}
        </div>

        {/* Mobile back link */}
        <li>
          <Link className={clsx('flex items-center gap-x-2.5 text-body-8 font-semibold lg:hidden', {
          hidden: !showMobileBackLink
        })} href={previousItem.url || pathname} data-sentry-element="Link" data-sentry-source-file="index.tsx">
            <ChevronDownIcon className="h-4 w-4 rotate-90" data-sentry-element="ChevronDownIcon" data-sentry-source-file="index.tsx" />
            <span className="uppercase">
              {t('breadcrumbs.backToPreviousBreadcrumbLabel', {
              previousBreadcrumbLabel: previousItem.label || ''
            })}
            </span>
          </Link>
        </li>
      </ul>
    </div>;
};
export default Breadcrumbs;